import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-refresh token on 401 responses
let isRefreshing = false;
let refreshQueue = [];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login') && !originalRequest.url?.includes('/auth/refresh')) {
      originalRequest._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          isRefreshing = false;
          refreshQueue.forEach((cb) => cb());
          refreshQueue = [];
          return axios(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshQueue = [];
          return Promise.reject(error);
        }
      }
      return new Promise((resolve) => {
        refreshQueue.push(() => resolve(axios(originalRequest)));
      });
    }
    return Promise.reject(error);
  }
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pull permissions for the current user. Resolved fresh on every login /
  // route remount so role edits in the admin UI apply without re-login.
  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/permissions`, { withCredentials: true });
      setPermissions(data?.permissions || []);
    } catch (_e) {
      setPermissions([]);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      await fetchPermissions();
    } catch (error) {
      setUser(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      await fetchPermissions();
    } catch (error) {
      // User not authenticated — expected on first load
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      await fetchPermissions();
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const register = async (email, password, fullName, phone, city, newsletter, termsVersion, extras = {}) => {
    try {
      const payload = {
        email, password,
        full_name: fullName,
        phone, city, newsletter, terms_version: termsVersion,
      };
      if (extras.ui_language) payload.ui_language = extras.ui_language;
      const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      setUser(data);
      await fetchPermissions();
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setPermissions([]);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Convenience: `hasPermission('view_users')` and `hasAny([...])`.
  const hasPermission = useCallback((key) => permissions.includes(key), [permissions]);
  const hasAnyPermission = useCallback(
    (keys) => Array.isArray(keys) && keys.some((k) => permissions.includes(k)),
    [permissions]
  );

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    setUser,
    permissions,
    hasPermission,
    hasAnyPermission,
    refreshPermissions: fetchPermissions,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isModerator: user?.role === 'moderator',
    isStaff: user?.role === 'admin' || user?.role === 'moderator'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;